import React, { useContext, useMemo, useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Button, Dropdown, Label, TableCell, TableRow, Icon } from 'semantic-ui-react'
import _ from 'lodash'

import { languageOption } from 'organisms/FeedbackFilterGroup/constants'
import { topicColor } from 'shared/constants/color'
import { Tag } from 'components/Tag'
import Popup from 'shared/components/Popup'
import MultiSurveyLanguageDropdown from 'components/MultiSurveyLanguageDropdown'
import useModal from 'hooks/useModal'
import useWorkspace from 'hooks/useWorkspace'
import ConfirmDeletionModal from 'components/Modals/ConfirmDeletionModal'
import { SurveyConfigurationContext } from 'organisms/SurveyConfiguration'
import { LanguageImportModal } from 'components/LanguageImportModal'
import SemanticListTable from 'shared/components/Table/SemanticListTable'

const SUCCESSFUL_DELETE_MESSAGE = 'Language deleted successfully.'

export default function SurveyLanguageTable({
  surveyLanguages: initialSurveyLanguages,
  onChangeSurveyLanguage,
  onChangeDefaultSurveyLanguage,
}) {
  const {
    Modal: DeletionModal,
    open: openDeletionModal,
    isOpen: isOpenDeletionModal,
    close: closeDeletionModal,
  } = useModal()

  const [surveyLanguages, setSurveyLanguages] = useState([])
  const [search, setSearch] = useState('')
  const {
    Modal: ImportModal,
    open: openImportModal,
    close: closeImportModal,
    isOpen: isImportModalOpen,
  } = useModal()
  const { workspaceToken } = useWorkspace()
  const { multiSurveyLanguages, surveyName, surveyProps, submitLanguageAction } = useContext(
    SurveyConfigurationContext
  )

  const surveyId = surveyProps?.data.id
  const surveyToken = surveyProps?.data.token
  const refetchSurvey = surveyProps.callApi

  const [selectedLanguage, setSelectedLanguage] = useState(null)
  useEffect(() => {
    setSurveyLanguages(initialSurveyLanguages)
  }, [initialSurveyLanguages])

  const debouncedsubmitLanguageAction = useCallback(
    _.debounce((config) => {
      submitLanguageAction(config)
    }, 500),
    [submitLanguageAction]
  )

  const handleAddLanguage = (languageCode) => {
    const newLang = {
      language: languageCode,
      is_active: false,
      is_default: false,
      is_translated: false,
    }

    const newSurveyLanguages = [...surveyLanguages, newLang]
    setSurveyLanguages(newSurveyLanguages)
    onChangeSurveyLanguage(newSurveyLanguages)
    setSearch('')
    debouncedsubmitLanguageAction({
      patchingConfigurationData: {
        is_multilingual_survey_enabled: true,
        survey_languages: newSurveyLanguages,
        default_language: newSurveyLanguages.find((lang) => lang.is_default)?.language || '',
      },
    })
  }

  const handleDelete = () => {
    if (!selectedLanguage) {
      return
    }
    const newSurveyLanguages = surveyLanguages.filter((lang) => lang.language !== selectedLanguage)
    setSurveyLanguages(newSurveyLanguages)
    onChangeSurveyLanguage(newSurveyLanguages)
    closeDeletionModal()
    submitLanguageAction({
      patchingConfigurationData: {
        is_multilingual_survey_enabled: true,
        survey_languages: newSurveyLanguages,
        default_language: newSurveyLanguages.find((lang) => lang.is_default)?.language || '',
      },
      successMessage: SUCCESSFUL_DELETE_MESSAGE,
    })
  }

  const handleToggleActive = (languageCode) => {
    const newSurveyLanguages = surveyLanguages.map((lang) => {
      if (lang.language === languageCode) {
        return { ...lang, is_active: !lang.is_active }
      } else {
        return lang
      }
    })

    setSurveyLanguages(newSurveyLanguages)
    onChangeSurveyLanguage(newSurveyLanguages)
    submitLanguageAction({
      patchingConfigurationData: {
        is_multilingual_survey_enabled: true,
        survey_languages: newSurveyLanguages,
        default_language: newSurveyLanguages.find((lang) => lang.is_default)?.language || '',
      },
    })
  }

  const handleSetDefault = (languageCode) => {
    const newSurveyLanguages = surveyLanguages.map((lang) => {
      if (lang.language === languageCode) {
        return { ...lang, is_default: true }
      }
      if (lang.is_default) {
        return { ...lang, is_default: false, is_translated: true }
      }
      return { ...lang, is_default: false }
    })

    setSurveyLanguages(newSurveyLanguages)
    onChangeDefaultSurveyLanguage(newSurveyLanguages)
    submitLanguageAction({
      patchingConfigurationData: {
        is_multilingual_survey_enabled: true,
        survey_languages: newSurveyLanguages,
        default_language: languageCode,
      },
    })
  }

  const allLanguages = languageOption || {}
  const addedLanguageCodes = surveyLanguages.map((l) => l.language)
  const availableLanguages = Object.entries(allLanguages).filter(
    ([, code]) => !addedLanguageCodes.includes(code)
  )
  const filteredLanguages = availableLanguages.filter(([name]) =>
    name.toLowerCase().includes(search.toLowerCase())
  )

  const sortedLanguages = useMemo(() => {
    return [...surveyLanguages].sort((a, b) => {
      if (a.is_default && !b.is_default) {
        return -1
      }
      if (b.is_default && !a.is_default) {
        return 1
      }
      if (a.is_active !== b.is_active) {
        return a.is_active ? -1 : 1
      }
      const language_label_a = displayLanguageLabel(a.language)
      const language_label_b = displayLanguageLabel(b.language)
      return language_label_a.localeCompare(language_label_b)
    })
  }, [surveyLanguages])

  const actionButton = (
    <div className="flex items-baseline float-right gap-1 align-baseline">
      <MultiSurveyLanguageDropdown
        search={search}
        setSearch={setSearch}
        handleAddLanguage={handleAddLanguage}
        filteredLanguages={filteredLanguages}
      />
      <Button primary size="small" className="!mr-0" onClick={openImportModal}>
        Import translations
      </Button>
    </div>
  )
  return (
    <div className="sub-menu-indent">
      <SemanticListTable title="Languages" actionSection={actionButton}>
        {sortedLanguages.map((item) => (
          <TableRow key={item.id}>
            <TableCell className="single-column-content">
              {displayActiveStatus(item.is_active)}
              {displayLanguageLabel(item.language)}
              {item.is_default && <Label basic>Default language</Label>}
              {!item.is_default && !item.is_translated && (
                <Popup
                  basic
                  content="Needs translations to use"
                  position="right center"
                  floating
                  on="hover"
                >
                  <span className="inline-block pointer-events-auto !cursor-default">
                    <Tag
                      key={`language-${item.language}`}
                      name={'Incomplete'}
                      className="text-center !pointer-events-auto !m-0"
                      color={topicColor['neutral']}
                      size="medium"
                      whiteFont={false}
                    />
                  </span>
                </Popup>
              )}
            </TableCell>
            <TableCell className="column-align-right">
              <LanguageOptionButton
                language={item.language}
                isDefault={item.is_default}
                isActive={item.is_active}
                isTranslated={item.is_translated}
                onDelete={() => {
                  setSelectedLanguage(item.language)
                  openDeletionModal()
                }}
                onToggleActive={() => handleToggleActive(item.language)}
                onSetDefault={() => handleSetDefault(item.language)}
              />
            </TableCell>
          </TableRow>
        ))}
      </SemanticListTable>
      {isOpenDeletionModal && (
        <ConfirmDeletionModal
          modal={DeletionModal}
          title="Delete language"
          loading={false}
          description="Deleting this language will permanently remove it from your survey. Responses already collected will remain in reporting."
          buttonLabel="Delete language"
          close={closeDeletionModal}
          handleDelete={handleDelete}
        />
      )}
      {isImportModalOpen && (
        <LanguageImportModal
          modal={ImportModal}
          close={closeImportModal}
          survey={{ token: surveyToken, name: surveyName, id: surveyId }}
          workspaceToken={workspaceToken}
          refetchSurvey={refetchSurvey}
          defaultLanguage={multiSurveyLanguages.find((lang) => lang.is_default)?.language}
          surveyLanguages={multiSurveyLanguages.map((lang) => lang.language)}
        />
      )}
    </div>
  )
}

SurveyLanguageTable.propTypes = {
  surveyLanguages: PropTypes.array,
  onChangeSurveyLanguage: PropTypes.func.isRequired,
  onChangeDefaultSurveyLanguage: PropTypes.func.isRequired,
}

const displayLanguageLabel = (value) => {
  return Object.keys(languageOption).find((key) => languageOption[key] === value)
}

const displayActiveStatus = (is_active) => {
  return (
    <Popup
      basic
      content={
        is_active ? 'Active and visible to respondents' : 'Inactive and hidden from respondents'
      }
      position={'left center'}
      size="mini"
      trigger={
        <Icon size="small" color={is_active ? 'green' : 'grey'} className="!mr-0">
          <i className={`${is_active ? 'fas fa-circle-small' : 'fal fa-circle'}`} />
        </Icon>
      }
    />
  )
}

const LanguageOptionButton = ({
  isDefault,
  isActive,
  isTranslated,
  onDelete,
  onToggleActive,
  onSetDefault,
}) => {
  if (isDefault) {
    return null
  }

  const menuItems = [
    {
      show: true,
      disable: !isActive || !isTranslated,
      text: 'Set as default',
      icon: 'far fa-bookmark',
      onClick: onSetDefault,
      tooltipMessage: 'Complete all translations to set as default',
    },
    {
      show: !isActive,
      disable: !isTranslated,
      text: 'Activate',
      icon: 'fas fa-eye',
      onClick: onToggleActive,
      tooltipMessage: 'Complete all translations to activate',
    },
    {
      show: isActive,
      disable: false,
      text: 'Deactivate',
      icon: 'fas fa-eye-slash',
      onClick: onToggleActive,
      tooltipMessage: '',
    },
    {
      show: true,
      disable: false,
      text: 'Delete',
      icon: 'far fa-trash-can',
      className: '!text-negative',
      tooltipMessage: '',
      onClick: () => {
        onDelete()
      },
    },
  ]

  return (
    <Dropdown
      icon="far fa-ellipsis-vertical"
      floating
      pointing="top right"
      className="widget-toolbar-dropdown"
    >
      <Dropdown.Menu>
        {menuItems
          .filter((item) => item.show)
          .map(({ text, icon, onClick, disable, tooltipMessage, className }) => (
            <>
              {disable ? (
                <Popup basic content={tooltipMessage} position="left center" floating on="hover">
                  <Dropdown.Item
                    className="!opacity-50 !cursor-default"
                    key={text}
                    text={text}
                    icon={icon}
                  />
                </Popup>
              ) : (
                <Dropdown.Item
                  key={text}
                  className={className}
                  text={text}
                  icon={icon}
                  onClick={onClick}
                />
              )}
            </>
          ))}
      </Dropdown.Menu>
    </Dropdown>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SurveyLanguageTable/index.js